<template>
    <div class="dashboard-root">

        <!-- ── Toolbar / DatePicker ─────────────────────────────────── -->
        <div class="dash-toolbar">
            <div class="date-picker-wrap dp-input-wrapper">
                <span class="dp-cal-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                        <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                </span>
                <DatePicker
                    v-model="datePicker"
                    dateFormat="dd/mm/yy"
                    placeholder="Selecionar data"
                />
            </div>
        </div>

        <!-- ── Top Grid: 2 gráficos + 2 KPI cards ──────────────────── -->
        <div class="top-grid">
            <div class="dash-card">
                <h4 class="card-title">CONCLUÍDOS MENSAL</h4>
                <ExecActiveBarChart :rawData="concluidosMes" barColor="#a855f7" />
                <div v-if="isLoading.concluidosMes" class="chart-loader">
                    <ProgressSpinner style="width: 40px; height: 40px" strokeWidth="6" />
                    <span>CARREGANDO GRÁFICO...</span>
                </div>
            </div>

            <div class="dash-card">
                <h4 class="card-title">CONCLUÍDOS DIÁRIO</h4>
                <ExecActiveBarChart :rawData="concluidosDia" barColor="#10b981" />
                <div v-if="isLoading.concluidosDia" class="chart-loader">
                    <ProgressSpinner style="width: 40px; height: 40px" strokeWidth="6" />
                    <span>CARREGANDO GRÁFICO...</span>
                </div>
            </div>

            <div class="kpi-column">
                <ExecKpiCard
                    title="Pendências"
                    :value="infosGerais.totalPendencias"
                    type="warning"
                />
                <ExecKpiCard
                    title="Cancelados"
                    :value="infosGerais.totalCancelados"
                    type="danger"
                />
            </div>
        </div>

        <!-- ── Bottom Grid: 3 gráficos ──────────────────────────────── -->
        <div class="bot-grid">
            <div class="dash-card">
                <h4 class="card-title">CONTRATOS ATUAIS</h4>
                <ExecHorizontalBarChart :rawData="qtdEtapas" />
                <div v-if="isLoading.qtdEtapas" class="chart-loader">
                    <ProgressSpinner style="width: 40px; height: 40px" strokeWidth="6" />
                    <span>CARREGANDO GRÁFICO...</span>
                </div>
            </div>

            <div class="dash-card">
                <h4 class="card-title">AGENDAMENTOS DIÁRIOS</h4>
                <ExecInteractiveAreaChart :rawData="agendamentosDia" />
                <div v-if="isLoading.agendamentosDia" class="chart-loader">
                    <ProgressSpinner style="width: 40px; height: 40px" strokeWidth="6" />
                    <span>CARREGANDO GRÁFICO...</span>
                </div>
            </div>

            <div class="dash-card">
                <h4 class="card-title">BACKLOG</h4>
                <small class="card-subtitle">Contratos em etapa de lead e cotação</small>
                <ExecActiveBarChart
                    :rawData="backlog"
                    :barColor="['#3b82f6', '#10b981', '#f59e0b', '#ef4444']"
                />
                <div v-if="isLoading.backlog" class="chart-loader">
                    <ProgressSpinner style="width: 40px; height: 40px" strokeWidth="6" />
                    <span>CARREGANDO GRÁFICO...</span>
                </div>
            </div>
        </div>

    </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import DatePicker from 'primevue/datepicker';
import { useToast } from 'primevue/usetoast';
import ProgressSpinner from 'primevue/progressspinner';

// Components
import ExecActiveBarChart from './components/ExecActiveBarChart.vue';
import ExecHorizontalBarChart from './components/ExecHorizontalBarChart.vue';
import ExecInteractiveAreaChart from './components/ExecInteractiveAreaChart.vue';
import ExecKpiCard from './components/ExecKpiCard.vue';

// Services
import Contrato from '@/service/contrato.js';
import Pendencia from '@/service/pendencia.js';
// State
const toast = useToast();
const datePicker = ref(new Date());
const isMounted = ref(false);
const isLoading = ref({
    concluidosMes: false,
    concluidosDia: false,
    agendamentosDia: false,
    qtdEtapas: false,
    backlog: false,
    totais: false
});

const infosGerais = ref({
    totalCancelados: 0,
    totalPendencias: 0,
});

const concluidosMes = ref({});
const concluidosDia = ref({});
const agendamentosDia = ref({});
const qtdEtapas = ref({});
const backlog = ref({});

// Lifecycle
onMounted(() => {
    isMounted.value = true;
    onDateChange();
    loadEtapasTotal();
});

watch(datePicker, () => {
    if (!isMounted.value) {
        return;
    }
    onDateChange();
});

// Methods
const onDateChange = () => {
    loadConcluidoMensal();
    loadConcluidoDiario();
    loadAgendamentoDiario();
    loadTotais();
    loadBacklog();
};

const loadConcluidoMensal = async () => {
    isLoading.value.concluidosMes = true;
    const dateStr = datePicker.value.toISOString().split('T')[0];
    try {
        const response = await Contrato.concluidosMensal(`data=${dateStr}`);
        const data = response.data.data;
        const labels = response.data.labels;

        concluidosMes.value = {
            labels,
            datasets: [{ label: 'Concluídos', data: data.map(item => item.concluido) }]
        };
    } catch (e) {
        console.error("Erro loadConcluidoMensal", e);
        toast.add({ severity: 'error', summary: 'Erro', detail: 'Falha ao carregar Concluídos Mensal', life: 5000 });
    } finally {
        isLoading.value.concluidosMes = false;
    }
};

const loadConcluidoDiario = async () => {
    isLoading.value.concluidosDia = true;
    const dateStr = datePicker.value.toISOString().split('T')[0];
    try {
        const response = await Contrato.concluidosDiario(`data=${dateStr}`);
        const data = response.data.data;
        const labels = response.data.labels;

        concluidosDia.value = {
            labels,
            datasets: [{ label: 'Concluídos', data: data.map(item => item.concluido) }]
        };
    } catch (e) {
        console.error("Erro loadConcluidoDiario", e);
        toast.add({ severity: 'error', summary: 'Erro', detail: 'Falha ao carregar Concluídos Diário', life: 5000 });
    } finally {
        isLoading.value.concluidosDia = false;
    }
};

const loadAgendamentoDiario = async () => {
    isLoading.value.agendamentosDia = true;
    const dateStr = datePicker.value.toISOString().split('T')[0];
    try {
        const response = await Contrato.agendamentosDiario(`data=${dateStr}`);
        const data = response.data.data;
        const labels = response.data.labels;

        agendamentosDia.value = {
            labels,
            datasets: [{ label: 'Agendamentos', data: data.map(item => item.agendado) }]
        };
    } catch (e) {
        console.error("Erro loadAgendamentoDiario", e);
        toast.add({ severity: 'error', summary: 'Erro', detail: 'Falha ao carregar Agendamentos Diário', life: 5000 });
    } finally {
        isLoading.value.agendamentosDia = false;
    }
};

const loadBacklog = async () => {
    isLoading.value.backlog = true;
    const dateStr = datePicker.value.toISOString().split('T')[0];
    try {
        const response = await Contrato.backlog(`data=${dateStr}`);
        const data = response.data.data;

        const labels = ["5 dias", "15 dias", "30 dias", "45+ dias"];
        const valores = [
            data.dias_5,
            data.dias_15,
            data.dias_30,
            data.dias_45_plus
        ];

        backlog.value = {
            labels,
            datasets: [{ label: 'Backlog', data: valores }]
        };
    } catch (e) {
        console.error("Erro loadBacklog", e);
        toast.add({ severity: 'error', summary: 'Erro', detail: 'Falha ao carregar Backlog', life: 5000 });
    } finally {
        isLoading.value.backlog = false;
    }
};

const loadEtapasTotal = async () => {
    isLoading.value.qtdEtapas = true;
    try {
        const response = await Contrato.etapasQuantidade();
        const data = response.data.data;

        const lead = data.find(item => item.id === 'lead');
        const cotacao = data.find(item => item.id === 'cotacao');
        const provedor = data.find(item => item.id === 'provedor');
        const assinatura = data.find(item => item.id === 'assinatura');

        const labels = ["Leads", "Provedor e Assinatura", "Cotação"];
        const valores = [
            lead ? lead.total : 0,
            (provedor ? provedor.total : 0) + (assinatura ? assinatura.total : 0),
            cotacao ? cotacao.total : 0
        ];

        qtdEtapas.value = {
            labels,
            datasets: [{ label: 'Contratos', data: valores }]
        };
    } catch (e) {
        console.error("Erro loadEtapasTotal", e);
        toast.add({ severity: 'error', summary: 'Erro', detail: 'Falha ao carregar Contratos Atuais', life: 5000 });
    } finally {
        isLoading.value.qtdEtapas = false;
    }
};

const loadTotais = async () => {
    isLoading.value.totais = true;
    const dateStr = datePicker.value.toISOString().split('T')[0];
    try {
        const respCancelado = await Contrato.totalCancelado(`data=${dateStr}`);
        infosGerais.value.totalCancelados = respCancelado.data.total;

        // KPI: pendências abertas nas categorias Cliente / Valor Alto (sem filtrar por dia —
        // o filtro por data única zerava o total na maioria dos casos).
        const respPendencia = await Pendencia.qtdTotal(
            `filter[pen.categoria_id][]=3&filter[pen.categoria_id][]=4&filter[pen.resolvido]=0`
        );
        infosGerais.value.totalPendencias = respPendencia.data.total;
    } catch (e) {
        console.error("Erro loadTotais", e);
        toast.add({ severity: 'error', summary: 'Erro', detail: 'Falha ao carregar KPIs totais', life: 5000 });
    } finally {
        isLoading.value.totais = false;
    }
};
</script>

<style scoped>
/* ── Root ────────────────────────────────────────────────────── */
.dashboard-root {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 0 2px 16px;
    min-height: 100%;
}

/* ── Toolbar ─────────────────────────────────────────────────── */
.dash-toolbar {
    display: flex;
    justify-content: flex-end;
    padding: 4px 0;
}

/* ── Date Picker wrap ───────────────────────────────────── */
.date-picker-wrap {
    position: relative;
    display: flex;
    align-items: center;
}
.dp-cal-icon {
    position: absolute;
    left: 11px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    z-index: 2;
    color: #6b7280;
    display: flex;
}
.dp-cal-icon svg {
    width: 15px;
    height: 15px;
}

/* Input nativo do DatePicker */
.dp-input-wrapper :deep(input) {
    background: #1a1a1a !important;
    color: #e6edf3 !important;
    border: 1px solid #2a2a2a !important;
    border-radius: 8px !important;
    padding: 7px 12px 7px 36px !important;
    font-size: 13px !important;
    font-weight: 500 !important;
    width: 148px;
    cursor: pointer;
    transition: border-color 160ms ease !important;
    caret-color: #f97316;
}
.dp-input-wrapper :deep(input)::placeholder {
    color: #6b7280;
}
.dp-input-wrapper :deep(input):focus {
    outline: none !important;
    border-color: #f97316 !important;
    box-shadow: 0 0 0 2px rgba(249,115,22,.18) !important;
}

/* Botão de ícone embutido do PrimeVue (removemos o nativo) */
:deep(.dp-root .p-datepicker-trigger) {
    display: none !important;
}

/* Painel flutuante */
:deep(.dp-panel),
:deep(.p-datepicker-panel) {
    background: #1e1e1e !important;
    border: 1px solid #2a2a2a !important;
    border-radius: 12px !important;
    box-shadow: 0 16px 48px rgba(0,0,0,.6) !important;
    overflow: hidden;
    margin-top: 6px;
    padding: 0 !important;
}

/* Header do calendário (mês/ano + setas) */
:deep(.p-datepicker-header) {
    background: #242424 !important;
    border-bottom: 1px solid #2a2a2a !important;
    padding: 10px 14px !important;
    display: flex;
    align-items: center;
    gap: 6px;
}
:deep(.p-datepicker-prev-button),
:deep(.p-datepicker-next-button) {
    background: transparent !important;
    border: 1px solid #2a2a2a !important;
    border-radius: 6px !important;
    color: #9ca3af !important;
    width: 28px !important;
    height: 28px !important;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 140ms ease, border-color 140ms ease !important;
}
:deep(.p-datepicker-prev-button:hover),
:deep(.p-datepicker-next-button:hover) {
    background: #2a2a2a !important;
    border-color: #f97316 !important;
    color: #f97316 !important;
}

/* Seletor de mês e ano */
:deep(.p-datepicker-select-month),
:deep(.p-datepicker-select-year) {
    background: transparent !important;
    border: none !important;
    color: #e6edf3 !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    cursor: pointer;
}
:deep(.p-datepicker-select-month:hover),
:deep(.p-datepicker-select-year:hover) {
    color: #f97316 !important;
}

/* Cabeçalho dos dias (S M T W T F S) */
:deep(.p-datepicker-weekday) {
    color: #6b7280 !important;
    font-size: 11px !important;
    font-weight: 600 !important;
    padding: 6px 0 !important;
}

/* Células dos dias */
:deep(.p-datepicker-day) {
    border-radius: 6px !important;
    color: #d1d5db !important;
    transition: background 120ms ease !important;
    font-size: 13px !important;
}
:deep(.p-datepicker-day:hover) {
    background: #2a2a2a !important;
    color: #fff !important;
}
:deep(.p-datepicker-day-selected) {
    background: #f97316 !important;
    color: #fff !important;
    font-weight: 700 !important;
}
:deep(.p-datepicker-day.p-datepicker-today > span) {
    background: transparent !important;
    border: 1px solid #f97316 !important;
    color: #f97316 !important;
    font-weight: 700 !important;
}
/* dias de outro mês */
:deep(.p-datepicker-other-month .p-datepicker-day) {
    color: #3f3f3f !important;
}

/* ── Generic card ────────────────────────────────────────────── */
.dash-card {
    position: relative;
    background: #1a1a1a;
    border: 1px solid #2a2a2a;
    border-radius: 8px;
    padding: 14px 16px;
    min-width: 0;
    transition: border-color 0.2s;
    overflow: hidden;
}
.dash-card:hover {
    border-color: #444444;
}

.chart-loader {
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(26, 26, 26, 0.7);
    backdrop-filter: blur(2px);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 10;
}
.chart-loader span {
    margin-top: 12px;
    font-size: 0.85rem;
    font-weight: 700;
    color: #f97316;
    letter-spacing: 0.05em;
}

.card-title {
    margin: 0 0 12px;
    font-size: 0.82rem;
    font-weight: 700;
    color: #f97316;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.card-subtitle {
    display: block;
    font-size: 0.75rem;
    color: #6b7280;
    margin-bottom: 10px;
}

/* ── KPI Column ──────────────────────────────────────────────── */
.kpi-column {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

/* ── Top Grid: 2 charts + 1 KPI column ───────────────────────── */
.top-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 0.55fr;
    gap: 12px;
}

/* ── Bottom Grid: 3 charts ───────────────────────────────────── */
.bot-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
}

/* ── Responsive ──────────────────────────────────────────────── */
@media (max-width: 1200px) {
    .top-grid {
        grid-template-columns: 1fr 1fr;
    }
    .kpi-column {
        grid-column: span 2;
        flex-direction: row;
    }
    .kpi-column > * {
        flex: 1;
    }
    .bot-grid {
        grid-template-columns: 1fr 1fr;
    }
}

@media (max-width: 768px) {
    .top-grid,
    .bot-grid {
        grid-template-columns: 1fr;
    }
    .kpi-column {
        grid-column: span 1;
        flex-direction: column;
    }
}
</style>
